import React, { useEffect, useState } from "react";
import { X, Upload, Trash2, Plus, Loader2, Image as ImageIcon, Languages } from "lucide-react";
import { toast } from "sonner";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { useAutoTranslate } from "@/hooks/useAutoTranslate";

const CATEGORIES_BOB = ["clothing", "perfumes", "cosmetics", "others"];
const SHEIN_SUBS = ["women", "men", "kids", "shoes", "accessories", "beauty", "phone_accessories", "others"];

/**
 * Modal form for creating or editing a product. The same form is reused
 * for both flows; when `existing` is provided, the form pre-fills fields
 * from the existing JS/DB product and submits via PUT instead of POST.
 *
 * Wire-level note: for JS-defined products only the FIELDS the operator
 * touches are sent as an override patch — untouched fields fall back to
 * the JS source (`originalsBaseline`). This keeps overrides minimal.
 */
export default function AdminProductForm({ open, onClose, existing, apiFetch, onSaved }) {
  const isEdit = !!existing;
  const initial = existing || {};
  const { translate, busy: translating } = useAutoTranslate();

  const [id, setId] = useState(initial.id || "");

  // For NEW products: fetch the next auto-generated id once on open and
  // show it as a read-only placeholder. The form submits with empty id
  // and the backend re-derives the final value so concurrent creates
  // never collide.
  useEffect(() => {
    if (!open || isEdit || id) return;
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch("/admin/products/next-id");
        if (!res.ok) return;
        const data = await res.json();
        if (alive && data?.id) setId(data.id);
      } catch { /* fall back to empty — backend still auto-assigns */ }
    })();
    return () => { alive = false; };
  }, [open, isEdit, apiFetch, id]);
  const [nameEn, setNameEn] = useState(initial.name?.en || "");
  const [nameAr, setNameAr] = useState(initial.name?.ar || "");
  const [nameFr, setNameFr] = useState(initial.name?.fr || "");
  const [descEn, setDescEn] = useState(initial.description?.en || "");
  const [descAr, setDescAr] = useState(initial.description?.ar || "");
  const [descFr, setDescFr] = useState(initial.description?.fr || "");
  const [colorEn, setColorEn] = useState(initial.color?.en || "");
  const [colorAr, setColorAr] = useState(initial.color?.ar || "");
  const [colorFr, setColorFr] = useState(initial.color?.fr || "");
  const [category, setCategory] = useState(initial.category || "others");
  const [sheinCategory, setSheinCategory] = useState(initial.sheinCategory || "women");
  const [originalPrice, setOriginalPrice] = useState(initial.originalPrice ?? "");
  const [discountPct, setDiscountPct] = useState(initial.discountPct ?? 50);
  // Sizes: comma-separated `label[:qty]` pairs (e.g. "S:3, M:2, L:1")
  const [sizesText, setSizesText] = useState(
    Array.isArray(initial.sizes) && initial.sizes.length
      ? initial.sizes.map((s) => `${s.label}:${s.qty ?? 1}`).join(", ")
      : ""
  );
  const [quantity, setQuantity] = useState(initial.quantity ?? 1);
  const [images, setImages] = useState(Array.isArray(initial.images) ? initial.images : []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  // Lock body scroll while modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const parseSizes = () => {
    const raw = (sizesText || "").trim();
    if (!raw) return [];
    return raw.split(",").map((s) => {
      const [label, qty] = s.split(":").map((x) => x.trim());
      if (!label) return null;
      const parsed = qty === "" || qty == null ? 0 : Number(qty);
      return { label, qty: Number.isFinite(parsed) ? Math.max(0, parsed) : 0 };
    }).filter(Boolean);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await apiFetch("/admin/upload-image", { method: "POST", body: fd });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.detail || "Upload failed");
        }
        const data = await res.json();
        urls.push(data.url);
      }
      setImages((prev) => [...prev, ...urls]);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      // Allow re-uploading the same file by resetting the input.
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  // Auto-translate EN → AR + FR for the three customer-facing fields.
  // A field is considered "empty" when it is blank OR equals the English
  // source verbatim — that second case catches legacy products where the
  // old save handler copied EN into AR/FR as a fallback. Genuine custom
  // translations entered by the operator are still preserved.
  const isEmptyOrEqualToEn = (val, en) => {
    const v = (val || "").trim();
    const e = (en || "").trim();
    return !v || v === e;
  };

  const handleAutoTranslate = async () => {
    if (!nameEn.trim() && !descEn.trim() && !colorEn.trim()) {
      toast.error("Enter at least the English name first.");
      return;
    }
    const jobs = [];
    if (nameEn.trim())  jobs.push({ key: "name",  text: nameEn,  ctx: "product_name" });
    if (descEn.trim())  jobs.push({ key: "desc",  text: descEn,  ctx: "product_description" });
    if (colorEn.trim()) jobs.push({ key: "color", text: colorEn, ctx: "color" });

    // Count how many target slots actually need filling BEFORE we call the
    // LLM, so we can tell apart "everything was already translated" from
    // "translation API failed". Without this split, a Gemini 502 would
    // wrongly trigger the 'Nothing to fill' toast (BUG 3 regression).
    let targets = 0;
    for (const j of jobs) {
      if (j.key === "name") {
        if (isEmptyOrEqualToEn(nameAr, nameEn)) targets++;
        if (isEmptyOrEqualToEn(nameFr, nameEn)) targets++;
      } else if (j.key === "desc") {
        if (isEmptyOrEqualToEn(descAr, descEn)) targets++;
        if (isEmptyOrEqualToEn(descFr, descEn)) targets++;
      } else if (j.key === "color") {
        if (isEmptyOrEqualToEn(colorAr, colorEn)) targets++;
        if (isEmptyOrEqualToEn(colorFr, colorEn)) targets++;
      }
    }

    if (targets === 0) {
      toast.info("Nothing to fill — AR/FR are already set.");
      return;
    }

    // silent=true → don't show the inner hook's own error toast; the
    // outer handleAutoTranslate aggregates the result and shows a
    // single, contextual message instead.
    const results = await Promise.all(jobs.map((j) => translate(j.text, j.ctx, ["ar", "fr"], { silent: true })));
    let filled = 0;
    jobs.forEach((j, i) => {
      const r = results[i];
      if (!r) return;
      if (j.key === "name") {
        if (isEmptyOrEqualToEn(nameAr, nameEn) && r.ar) { setNameAr(r.ar); filled++; }
        if (isEmptyOrEqualToEn(nameFr, nameEn) && r.fr) { setNameFr(r.fr); filled++; }
      }
      if (j.key === "desc") {
        if (isEmptyOrEqualToEn(descAr, descEn) && r.ar) { setDescAr(r.ar); filled++; }
        if (isEmptyOrEqualToEn(descFr, descEn) && r.fr) { setDescFr(r.fr); filled++; }
      }
      if (j.key === "color") {
        if (isEmptyOrEqualToEn(colorAr, colorEn) && r.ar) { setColorAr(r.ar); filled++; }
        if (isEmptyOrEqualToEn(colorFr, colorEn) && r.fr) { setColorFr(r.fr); filled++; }
      }
    });

    if (filled > 0) {
      toast.success(`Filled ${filled} translation${filled > 1 ? "s" : ""}`);
    } else {
      // We had targets but the API gave us nothing — surface the real
      // cause (the inner `translate` hook already showed a network/error
      // toast; this one disambiguates from the legitimate "nothing to fill"
      // state).
      toast.error("Translation service unavailable — saved as English.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const sizes = parseSizes();
      // Send AR/FR exactly as the operator typed them. Empty fields go to
      // the backend as empty so `silent_translate.fill_translations` can
      // fill them via Gemini, and so the frontend `localized()` helper
      // can fall back to EN at render time without us baking EN into the
      // saved document (which would later defeat Auto Translate).
      const body = {
        // For NEW products: send empty id and let the backend pick the
        // next number. For edits: id stays in the URL path, never the body.
        id: isEdit ? undefined : (id.trim() || undefined),
        name: { en: nameEn, ar: nameAr, fr: nameFr },
        description: (descEn || descAr || descFr) ? { en: descEn, ar: descAr, fr: descFr } : undefined,
        color: (colorEn || colorAr || colorFr) ? { en: colorEn, ar: colorAr, fr: colorFr } : undefined,
        category,
        sheinCategory: category === "shein" ? sheinCategory : undefined,
        originalPrice: Number(originalPrice),
        discountPct: Number(discountPct) || 0,
        sizes: sizes.length ? sizes : undefined,
        quantity: sizes.length ? undefined : Math.max(0, Number.isFinite(Number(quantity)) ? Number(quantity) : 0),
        images,
        addedDate: initial.addedDate || new Date().toISOString().slice(0, 10),
        tags: initial.tags || ["new"],
      };

      const path = isEdit ? `/admin/products/${initial.id}` : `/admin/products`;
      const method = isEdit ? "PUT" : "POST";
      const res = await apiFetch(path, { method, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `${method} failed`);
      toast.success(isEdit ? "Product updated" : "Product created");
      onSaved && onSaved(data);
      onClose && onClose();
    } catch (err) {
      setError(err?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" data-testid="product-form-modal">
      <form onSubmit={submit} className="w-full max-w-2xl bg-[#141414] rounded-2xl border border-white/10 shadow-2xl my-4 sm:my-8 max-h-[calc(100vh-2rem)] flex flex-col">
        <header className="flex items-center justify-between p-4 sm:p-5 border-b border-white/5 bg-[#141414] rounded-t-2xl shrink-0">
          <h3 className="font-display text-base sm:text-lg font-bold truncate pr-2">{isEdit ? `Edit #${initial.id}` : "New product"}</h3>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={translating}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-[11px] font-bold disabled:opacity-50"
              title="Translate English to Arabic and French (only fills empty fields)"
              data-testid="form-auto-translate"
            >
              {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">Auto Translate</span>
            </button>
            <button type="button" onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="form-close"><X className="w-5 h-5" /></button>
          </div>
        </header>

        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {/* ID + Category */}
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Product ID"
              hint={isEdit ? "Cannot be changed" : "Auto-generated — next available number"}
            >
              <input
                value={id}
                onChange={(e) => setId(e.target.value)}
                disabled={isEdit || !isEdit /* always read-only here: edit locks it, create auto-fills it */}
                className="form-input bg-white/5 cursor-not-allowed"
                data-testid="form-id"
                placeholder={isEdit ? "" : "auto…"}
              />
            </Field>
            <Field label="Category *">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="form-input" data-testid="form-category">
                {[...CATEGORIES_BOB, "shein"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {category === "shein" && (
            <Field label="SHEIN subcategory *">
              <select value={sheinCategory} onChange={(e) => setSheinCategory(e.target.value)} className="form-input" data-testid="form-shein-cat">
                {SHEIN_SUBS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}

          {/* Names */}
          <Field label="Name (EN) *">
            <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} required className="form-input" data-testid="form-name-en" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name (AR)">
              <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} className="form-input" dir="rtl" data-testid="form-name-ar" />
            </Field>
            <Field label="Name (FR)">
              <input value={nameFr} onChange={(e) => setNameFr(e.target.value)} className="form-input" data-testid="form-name-fr" />
            </Field>
          </div>

          {/* Description (optional) */}
          <Field label="Description (EN)" hint="Optional">
            <textarea rows={2} value={descEn} onChange={(e) => setDescEn(e.target.value)} className="form-input" data-testid="form-desc-en" />
          </Field>

          {/* Color */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Color (EN)">
              <input value={colorEn} onChange={(e) => setColorEn(e.target.value)} className="form-input" data-testid="form-color-en" />
            </Field>
            <Field label="Color (AR)">
              <input value={colorAr} onChange={(e) => setColorAr(e.target.value)} className="form-input" dir="rtl" />
            </Field>
            <Field label="Color (FR)">
              <input value={colorFr} onChange={(e) => setColorFr(e.target.value)} className="form-input" />
            </Field>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Original price ($) *">
              <input type="number" min={0} step="0.01" value={originalPrice} onChange={(e) => setOriginalPrice(e.target.value)} required className="form-input" data-testid="form-price" />
            </Field>
            <Field label="Discount (%)" hint="0–99">
              <input type="number" min={0} max={99} value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} className="form-input" data-testid="form-discount" />
            </Field>
          </div>

          {/* Sizes / Quantity */}
          <Field label="Sizes" hint='Format: "S:3, M:2, L:1" — leave empty for single-SKU'>
            <input value={sizesText} onChange={(e) => setSizesText(e.target.value)} className="form-input font-mono" placeholder="S:3, M:2, L:1" data-testid="form-sizes" />
          </Field>
          {!sizesText.trim() && (
            <Field label="Stock quantity" hint="Used when no sizes provided">
              <input type="number" min={0} max={9999} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="form-input" data-testid="form-quantity" />
            </Field>
          )}

          {/* Images */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">Images</span>
              <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white text-xs font-semibold cursor-pointer">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload
                <input type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={handleUpload} className="hidden" data-testid="form-upload" disabled={uploading} />
              </label>
            </div>
            {images.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-24 rounded-xl border border-dashed border-white/10 text-white/30 text-xs">
                <ImageIcon className="w-5 h-5 mb-1" />
                No images yet — first one is the cover
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {images.map((src, i) => (
                  <div key={src + i} className="relative group aspect-square rounded-xl overflow-hidden border border-white/10 bg-black">
                    <img src={resolveAssetUrl(src)} alt="" className="w-full h-full object-cover" />
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 text-[9px] font-bold uppercase tracking-wider bg-[#F37021] text-white px-1.5 py-0.5 rounded">cover</span>
                    )}
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-600 flex items-center justify-center transition-colors" data-testid={`form-image-remove-${i}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="form-error">
              {error}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 sm:p-5 border-t border-white/5 bg-[#141414] rounded-b-2xl shrink-0">
          <button type="button" onClick={onClose} className="h-10 px-5 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={busy} className="h-10 px-6 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="form-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save changes" : "Create product"}
          </button>
        </footer>
      </form>

      <style>{`
        .form-input { width: 100%; height: 2.5rem; padding: 0 0.75rem; border-radius: 0.75rem;
          background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1);
          color: white; font-size: 0.875rem; }
        .form-input:focus { outline: none; border-color: #F37021; }
        textarea.form-input { height: auto; padding: 0.5rem 0.75rem; }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{label}</span>
      {hint && <span className="ms-2 text-[10px] text-white/30">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
